import { environment } from '../../../environments/environment';
import { ApiCategory, ApiProduct } from '../../models/api.models';
import { ModoDeSede } from '../../services/table.service';
import { PagerGroup } from '../../services/pager-config.service';
import { FilaDeCliente } from '../../services/clientes-del-mayorista.service';
import { FilaDeVendedor } from '../../services/vendedores.service';
import { RespuestaDeCrearOrden } from '../offline/http-order-sync.gateway';
import { RespuestaDePerfil } from '../perfil/perfil.model';
import { Familia } from '../../components/pos/venta-rapida/familias.service';

/**
 * Lo que el demo local se contesta a sí mismo. **Cada respuesta lleva el tipo con el que la caja lee al servidor de
 * verdad** (condición de ECM): si el contrato cambia en la caja, esto deja de compilar; y `respuestas-del-demo.spec.ts`
 * pasa cada una por el mismo lector que usa la caja. Un demo con datos que el servidor nunca manda es una fixture que
 * inventa el dato.
 *
 * Oficio: distribuidora de abarrotes (ECM). El perfil es la fila GLOBAL `mayorista` de
 * `inventario.perfiles_verticales` en staging, leída el 2026-09-18 (actualizada allí el 2026-09-09), copiada tal cual.
 */

export interface RespuestaLocal {
  status: number;
  body: unknown;
}

const producto = (id: string, nombre: string, precio: number, categoria: [string, string]): ApiProduct => ({
  idProduct: id, nameProduct: nombre, price: precio, active: true, categoryId: categoria[0], categoryName: categoria[1], codigos: [],
});
const GRANOS: [string, string] = ['demo-granos', 'Granos'];
const ACEITES: [string, string] = ['demo-aceites', 'Aceites'];
const DESPENSA: [string, string] = ['demo-despensa', 'Despensa'];
const PRODUCTOS: ApiProduct[] = [
  producto('demo-arroz', 'Arroz 500 g paca x 25', 62000, GRANOS),
  producto('demo-lenteja', 'Lenteja 500 g paca x 25', 71000, GRANOS),
  producto('demo-frijol', 'Fríjol 500 g paca x 25', 118000, GRANOS),
  producto('demo-aceite', 'Aceite 1 L caja x 12', 98000, ACEITES),
  producto('demo-aceite-3', 'Aceite 3 L caja x 4', 86000, ACEITES),
  producto('demo-azucar', 'Azúcar 1 kg paca x 25', 105000, DESPENSA),
  producto('demo-panela', 'Panela 500 g paca x 24', 58000, DESPENSA),
  producto('demo-sal', 'Sal 1 kg paca x 25', 32000, DESPENSA),
  producto('demo-cafe', 'Café molido 250 g caja x 24', 168000, DESPENSA),
  producto('demo-pasta', 'Pasta 250 g paca x 24', 44000, DESPENSA),
  producto('demo-harina', 'Harina de maíz 1 kg paca x 24', 79000, DESPENSA),
  producto('demo-atun', 'Atún 160 g caja x 48', 192000, DESPENSA),
];
export const CATALOGO_DEL_DEMO: ApiCategory[] = [GRANOS, ACEITES, DESPENSA].map(([id, nombre]) => ({
  idCategory: id, nameCategory: nombre, products: PRODUCTOS.filter((p) => p.categoryId === id),
}));

/** Venta directa, con tirilla por el navegador: la caja de un mayorista, sin mesas ni rastreadores. */
export const MODO_DEL_DEMO: ModoDeSede = {
  posMode: 'PLAZOLETA', restaurante: false, flujoDeVenta: 'DIRECTO', usaMesas: false, usaRastreador: false,
  imprimeTirilla: true, baseCaja: null, tienePinRegistro: false,
};

/**
 * Dos clientes con cupo y sin deuda. Sin deuda a propósito: con deuda la caja ofrece «Cobrar», que es la cartera, y la
 * cartera no está en el demo (ECM).
 */
export const CLIENTES_DEL_DEMO: FilaDeCliente[] = [
  { documento: '900111222', nombre: 'Tienda Doña Rosa', telefono: '3001112233', plazo_dias: 30, activo: true,
    lista_precio_id: null, lista: null, deuda: 0, cupo: 2000000, en_insolvencia_desde: null },
  { documento: '901333444', nombre: 'Minimercado El Ahorro', telefono: '3004445566', plazo_dias: 15, activo: true,
    lista_precio_id: null, lista: null, deuda: 0, cupo: 800000, en_insolvencia_desde: null },
];

export const VENDEDORES_DEL_DEMO: FilaDeVendedor[] = [
  { id: 9001, email: 'ana@demo.invalid', nombre: 'Ana (demo)' },
  { id: 9002, email: 'luis@demo.invalid', nombre: 'Luis (demo)' },
];

export const PERFIL_DEL_DEMO: RespuestaDePerfil = {
  asignado: true,
  codigo: 'mayorista',
  nombre: 'Mayorista',
  descripcion: 'Vende por bulto a otros negocios. Lote, vencimiento y granel; bodegas en vez de sedes.',
  terminologia: {
    sede: { plural: 'bodegas', singular: 'bodega' },
    insumo: { plural: 'referencias', singular: 'referencia' },
    catalogo: { plural: 'productos', singular: 'productos' },
    producto: { plural: 'productos', singular: 'producto' },
    variantes: { plural: 'presentaciones', singular: 'presentación' },
    componente: { plural: 'piezas', singular: 'pieza' },
    componentes: { plural: 'combos', singular: 'combo' },
    ejemplo_insumo: { plural: 'Aceite Premier x 1 L', singular: 'Aceite Premier x 1 L' },
    ejemplo_producto: { plural: 'Aceite Premier caja x 12', singular: 'Aceite Premier caja x 12' },
    ejemplo_categoria: { plural: 'Aceites', singular: 'Aceites' },
    ejemplo_parecidos: { plural: '«Tornillo 1/4» y «Tornillo 3/8»', singular: '«Tornillo 1/4» y «Tornillo 3/8»' },
    ejemplo_presentacion: { plural: 'Caja x 12', singular: 'Caja x 12' },
  },
  tema: { acento: '#6d28d9', densidad: 'compacta', primario: '#101825' },
  capacidades: ['componentes', 'lote', 'vencimiento', 'granel', 'variantes'],
  rasgosPorDefecto: null,
  asignadoEn: '2026-09-18T00:00:00Z',
  fuente: 'demo',
  confianza: 1,
  asignadoPor: 'demo',
};

/** Sin familias: el demo no enseña variantes agrupadas. */
export const FAMILIAS_DEL_DEMO: Familia[] = [];

let siguienteOrden = 1;

/** `POST /orders/create`: acepta la venta y dice si se pasa del cupo, como el servidor. Sin deuda previa en el demo. */
export function crearOrdenEnElDemo(cuerpo: unknown): RespuestaDeCrearOrden {
  const venta = (cuerpo ?? {}) as { clienteDocumento?: string; condicionPago?: string; total?: number };
  const cliente = CLIENTES_DEL_DEMO.find((c) => c.documento === venta.clienteDocumento);
  const aCredito = venta.condicionPago === 'CREDITO';
  const cupo = cliente?.cupo == null ? null : Number(cliente.cupo);
  return {
    idOrder: siguienteOrden++,
    totalDiscrepancia: 0,
    excedeCupo: aCredito && cupo !== null ? Number(venta.total ?? 0) > cupo : null,
  };
}

const bajo = (url: string, base: string): string | null =>
  base && url.startsWith(base) ? url.slice(base.length).split('?')[0] : null;

/**
 * La respuesta para una petición a los servicios del POS, o `null` si el demo no la tiene (el interceptor la hace
 * fallar como «sin red», que la caja ya sabe tratar sin ventanas de error).
 */
export function respuestaDelDemo(metodo: string, url: string, cuerpo: unknown): RespuestaLocal | null {
  const mt = bajo(url, environment.apiBaseUrl);
  if (mt !== null) {
    if (metodo === 'GET' && mt === '/api/menu/categories-with-products') return { status: 200, body: CATALOGO_DEL_DEMO };
    if (metodo === 'GET' && mt === '/account/sites/mode') return { status: 200, body: MODO_DEL_DEMO };
    if (metodo === 'GET' && mt === '/account/pagers') return { status: 200, body: [] satisfies PagerGroup[] };
    if (metodo === 'GET' && mt === '/api/mayorista/clientes') return { status: 200, body: CLIENTES_DEL_DEMO };
    const ficha = /^\/api\/mayorista\/clientes\/([^/]+)$/.exec(mt);
    if (metodo === 'GET' && ficha) {
      const c = CLIENTES_DEL_DEMO.find((x) => x.documento === decodeURIComponent(ficha[1]));
      return c ? { status: 200, body: c } : null;
    }
    if (metodo === 'GET' && mt === '/account/users') return { status: 200, body: VENDEDORES_DEL_DEMO };
    if (metodo === 'POST' && mt === '/orders/create') return { status: 200, body: crearOrdenEnElDemo(cuerpo) };
    return null;
  }
  const inv = bajo(url, environment.inventoryApiUrl);
  if (inv !== null) {
    if (metodo === 'GET' && inv === '/perfil') return { status: 200, body: PERFIL_DEL_DEMO };
    if (metodo === 'GET' && inv === '/identidad/familias') return { status: 200, body: FAMILIAS_DEL_DEMO };
    return null;
  }
  return null;
}

/**
 * ¿Saldría esta petición de este navegador? Toda URL absoluta de otro origen: los servicios del POS y cualquier otra
 * que alguien añada mañana. Lo del propio origen (el bundle, `version.json`, las letras) no sale a ningún servicio.
 * Las bases de los servicios son absolutas en los tres entornos (medido el 18/09); una relativa pasaría como propia.
 */
export function saleDeEsteNavegador(url: string, origen: string): boolean {
  return /^https?:\/\//i.test(url) && !url.startsWith(origen + '/') && url !== origen;
}
